import React, { forwardRef, RefObject, useCallback, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { useCombinedRef } from '../hooks/useCombinedRef';
import { useClickOutside } from '../hooks/useClickOutside';
import PopperJS, { createPopper, Modifier, Placement } from '@popperjs/core';

type Props = React.HTMLAttributes<HTMLDivElement> & {
    /**
     * A ref to an element the popper should be positioned relatively to.
     */
    readonly referenceElement: RefObject<Element>;

    /**
     * Indicates whether the popper should be visible.
     */
    readonly visible?: boolean;

    /**
     * Specifies how the popper should be positioned relative to the reference element.
     */
    readonly placement: Placement;

    /**
     * Additional CSS classes for the popper element.
     */
    readonly className?: string;

    /**
     * Specifies the size of the gap between the reference element and the popper in pixels.
     * Defaults to 0 pixels.
     *
     * @see https://popper.js.org/docs/v2/modifiers/offset/
     */
    readonly gap?: number;

    /**
     * Specifies the displacement of the popper relative to the reference element in pixels.
     * The direction of the displacement is dependent on the placement of the popper.
     * Defaults to 0 pixels.
     *
     * @see https://popper.js.org/docs/v2/modifiers/offset/
     */
    readonly displacement?: number;

    /**
     * Called when the user clicks outside the popper.
     * Always passes `false` as an argument, such that a `setVisible` state setter could be directly used as a
     * callback.
     */
    readonly onClickOutside?: (visible: false) => void;

    /**
     * A ref to an element that is treated as an arrow pointing from the popper to the reference element.
     * This element will be automatically positioned by PopperJS.
     *
     * @see https://popper.js.org/docs/v2/modifiers/arrow/
     */
    readonly arrowElement?: RefObject<Element>;

    /**
     * Indicates whether the popper should rendered within a React Portal which is appended to the end of the <body>
     * element.
     */
    readonly portal?: boolean;

    /**
     * append Popper.js modifier
     */
    readonly modifiers?: Modifier<any, any>[];
};

export const Popper = forwardRef<HTMLDivElement, Props>(
    (
        {
            referenceElement,
            visible,
            placement,
            className,
            children,
            gap = 0,
            displacement = 0,
            onClickOutside,
            arrowElement,
            portal = true,
            modifiers = [],
            ...props
        },
        ref
    ) => {
        const popperElementRef = useCombinedRef<HTMLDivElement>(ref);
        const instanceRef = useRef<PopperJS.Instance>();

        useClickOutside({
            element: popperElementRef,
            handler: useCallback(() => onClickOutside?.(false), [onClickOutside]),
            ignoreElement: referenceElement
        });

        useLayoutEffect(() => {
            if (visible && referenceElement.current && popperElementRef.current) {
                instanceRef.current = createPopper(referenceElement.current, popperElementRef.current, {
                    placement,
                    modifiers: [
                        ...modifiers,
                        {
                            name: 'arrow',
                            options: {
                                element: arrowElement?.current
                            },
                            enabled: !!arrowElement
                        },
                        {
                            name: 'offset',
                            options: {
                                offset: [displacement, gap]
                            }
                        }
                    ]
                });
            }

            return () => {
                if (instanceRef.current) {
                    instanceRef.current.destroy();
                }
            };
        }, [
            referenceElement,
            popperElementRef,
            instanceRef,
            placement,
            visible,
            displacement,
            gap,
            arrowElement,
            modifiers
        ]);

        const popperElement = (
            <div {...props} className={className} ref={popperElementRef} hidden={!visible}>
                {visible && children}
            </div>
        );

        if (!portal) {
            return popperElement;
        }

        return ReactDOM.createPortal(popperElement, document.body);
    }
);
